// Apps registry client — first-class, user-scoped apps over the published
// *.view endpoints. Backs the Drive-like /apps explorer: ownership, rename,
// folders, sharing (private / shared-to-people / org-visible by link).
import { fetchData } from '../fetchapi/fetchapi';

const json = (method, body) => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

// { flow_project_id, org, me, apps: [...], folders: [...] }
// `projectSlug` (optional) lists another project's apps — the Board's
// cross-project New task dialog loads change-targets for whichever project
// is selected; the explicit header wins over the URL-derived one.
export async function listApps({ projectSlug } = {}) {
  return fetchData('apps', projectSlug
    ? { headers: { 'X-Cortex-Project': projectSlug, 'X-Cortex-Branch': 'main' } }
    : undefined);
}

// Access-checked resolve of one app by view name (share page / deep links).
export async function resolveApp(viewName) {
  return fetchData(`apps/resolve/${encodeURIComponent(viewName)}`);
}

// Sign in from the share page — the ORDINARY cortex login.
//
// A share recipient has a real account, typically an `app_user`: no
// organization, no project access. One identity, one password, nothing extra
// to manage. The card lives on the share page only so a visitor handed an app
// is never sent to cortex's front door for it.
export async function login({ email, password }) {
  return fetchData('auth/login', json('POST', { email, password }));
}

// Viewer options (share-page chrome): what app.viewer_chrome = NULL means.
// Shared by the share page (render) and the Viewer options dialog (edit).
export const VIEWER_CHROME_DEFAULTS = {
  chat: true,
  workflows: true,
  guides: true,
  pipeline: false,
  governance: false,
  docs: false,
};

// patch: { title?, folder_id?, visibility? } — title:null resets the rename,
// folder_id:null moves to root, visibility 'private' | 'org'.
export async function updateApp(id, patch) {
  return fetchData(`apps/${id}`, json('PUT', patch));
}

export async function removeApp(id) {
  return fetchData(`apps/${id}`, { method: 'DELETE' });
}

// Duplicate an app. Two scopes:
//   duplicateApp(id, { title })  — UI copy on the same fork (shared data/engine).
//   duplicateApp(id, { title, target_project_slug }) — FULL copy: engine
//     endpoints + a fresh data schema cloned into an existing target project
//     (create it first via createProject). Returns { app, project_slug?, engine? }.
export async function duplicateApp(id, body) {
  return fetchData(`apps/${id}/duplicate`, json('POST', body || {}));
}

export async function shareApp(id, email) {
  return fetchData(`apps/${id}/shares`, json('POST', { email }));
}

export async function unshareApp(id, userId) {
  return fetchData(`apps/${id}/shares/${userId}`, { method: 'DELETE' });
}

// Org member directory for the share picker: { members: [{id,email,name}] }
export async function listOrgMembers() {
  return fetchData('apps/org_members');
}

export async function createAppFolder(name, parentId = null) {
  return fetchData('app_folders', json('POST', { name, parent_id: parentId }));
}

export async function updateAppFolder(id, patch) {
  return fetchData(`app_folders/${id}`, json('PUT', patch));
}

export async function deleteAppFolder(id) {
  return fetchData(`app_folders/${id}`, { method: 'DELETE' });
}

// ── App requests (new app / change / bug fix → local-agent queue) ───────────
// { requests: [...], me } — caller's own requests; status 'open' filters to
// queued/claimed/running; all=true (admins) lists the whole org's queue.
export async function listAppRequests({ status, all } = {}) {
  const q = new URLSearchParams();
  if (status) q.set('status', status);
  if (all) q.set('all', '1');
  const qs = q.toString();
  return fetchData(`app_requests${qs ? `?${qs}` : ''}`);
}

// body: { kind: 'new_app'|'change'|'bug_fix'|…, title, details?, app_id?,
// initial_status? }. `projectSlug` (optional) files the request into that
// project instead of the one in the URL — the Board's cross-project "New task"
// dialog uses it; an explicit X-Cortex-Project header wins over the derived
// one in fetchapi, and the backend's per-project access gate validates it.
export async function createAppRequest(body, { projectSlug } = {}) {
  const opts = json('POST', body);
  if (projectSlug) {
    opts.headers = { ...opts.headers, 'X-Cortex-Project': projectSlug, 'X-Cortex-Branch': 'main' };
  }
  return fetchData('app_requests', opts);
}

export async function cancelAppRequest(id) {
  return fetchData(`app_requests/${id}`, { method: 'DELETE' });
}

// The form twin of the chat's feature checklist: given the kind and what the
// user typed so far (plus attached file NAMES), returns { groups: [{ label,
// options: [{ id, label, description, checked }] }] } for the request dialog
// to render as checkboxes. body: { kind, title?, details?, attachment_names?,
// exclude_tenancy? }.
export async function proposeRequestFeatures(body, { projectSlug } = {}) {
  const opts = json('POST', body);
  if (projectSlug) {
    opts.headers = { ...opts.headers, 'X-Cortex-Project': projectSlug, 'X-Cortex-Branch': 'main' };
  }
  return fetchData('app_requests/propose_features', opts);
}

// ── Agents (first-class per-project agents) ─────────────────────────────────
// { agents: [{ id, name, display_name, description, icon, visibility,
//   is_owner, owner, apps: [{id,view_name,title}], sources: [{project_slug,
//   source_id}], shares? }] } — only the ones visible to the caller. The
// backend syncs the registry from the live fork on list. Project-scoped via
// the X-Cortex-Project header (same as /api/apps).
// `projectSlug` — same cross-project override as listApps (Board dialog).
export async function listAgents({ projectSlug } = {}) {
  return fetchData('agents', projectSlug
    ? { headers: { 'X-Cortex-Project': projectSlug, 'X-Cortex-Branch': 'main' } }
    : undefined);
}

export async function updateAgentVisibility(id, visibility) {
  return fetchData(`agents/${id}`, json('PUT', { visibility }));
}

export async function shareAgent(id, email) {
  return fetchData(`agents/${id}/shares`, json('POST', { email }));
}

export async function unshareAgent(id, userId) {
  return fetchData(`agents/${id}/shares/${userId}`, { method: 'DELETE' });
}

// Fully remove an agent (owner/admin). Backend removes the registry entry.
export async function removeAgent(id) {
  return fetchData(`agents/${id}`, { method: 'DELETE' });
}
